import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useSection, resolveImage } from '../contexts/ContentContext';

/* Desktop nav item with optional dropdown */
function NavItem({ item, active }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!hasChildren) {
    return (
      <Link
        to={item.to}
        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
          active ? 'text-sky-700 bg-sky-50' : 'text-slate-600 hover:text-sky-700 hover:bg-sky-50/60'
        }`}
        data-testid={`nav-item-${item.label}`}
      >
        {item.label}
      </Link>
    );
  }

  const childActive = item.children.some((c) => c.to === location.pathname);
  const isActive = active || childActive;

  return (
    <div
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onFocus={openMenu}
      onBlur={scheduleClose}
    >
      <Link
        to={item.to}
        className={`inline-flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
          isActive ? 'text-sky-700 bg-sky-50' : 'text-slate-600 hover:text-sky-700 hover:bg-sky-50/60'
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => {
          // On desktop, allow navigating parent by click; but if we're just interacting with dropdown, keep hover behaviour.
        }}
        data-testid={`nav-parent-${item.label}`}
      >
        {item.label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </Link>

      {/* Bridge to prevent gap flicker */}
      <div
        className={`absolute left-0 right-0 top-full h-2 ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden="true"
      />

      <div
        role="menu"
        aria-label={`Sous-menu ${item.label}`}
        className={`absolute left-0 top-[calc(100%+8px)] min-w-[240px] p-2 bg-white rounded-2xl shadow-xl border border-sky-100 origin-top transition-all duration-200 ease-out ${
          open ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'
        }`}
      >
        <Link
          to={item.to}
          role="menuitem"
          className={`block px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
            location.pathname === item.to ? 'bg-sky-50 text-sky-700' : 'text-slate-700 hover:bg-sky-50/70 hover:text-sky-700'
          }`}
          data-testid={`nav-dropdown-${item.label}-parent`}
        >
          {item.label}
          <span className="block text-[10px] font-normal text-slate-400 mt-0.5">Vue d'ensemble</span>
        </Link>
        <div className="my-1 h-px bg-sky-100/70"></div>
        {item.children.map((child) => (
          <Link
            key={child.to + child.label}
            to={child.to}
            role="menuitem"
            className={`block px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              location.pathname === child.to ? 'bg-sky-50 text-sky-700' : 'text-slate-700 hover:bg-sky-50/70 hover:text-sky-700'
            }`}
            data-testid={`nav-dropdown-${child.label}`}
          >
            {child.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

/* Mobile nav item with optional collapsible sub-list */
function MobileNavItem({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  if (!hasChildren) {
    return (
      <Link
        to={item.to}
        onClick={onNavigate}
        className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
          location.pathname === item.to ? 'bg-sky-50 text-sky-700' : 'text-slate-700 hover:bg-sky-50'
        }`}
        data-testid={`mobile-nav-item-${item.label}`}
      >
        {item.label}
      </Link>
    );
  }

  const isActive = location.pathname === item.to || item.children.some((c) => c.to === location.pathname);

  return (
    <div>
      <div className="flex items-stretch">
        <Link
          to={item.to}
          onClick={onNavigate}
          className={`flex-1 px-4 py-3 rounded-l-xl text-sm font-medium transition-colors ${
            isActive ? 'bg-sky-50 text-sky-700' : 'text-slate-700 hover:bg-sky-50'
          }`}
          data-testid={`mobile-nav-parent-${item.label}`}
        >
          {item.label}
        </Link>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={`${open ? 'Fermer' : 'Ouvrir'} le sous-menu ${item.label}`}
          className={`px-3 rounded-r-xl transition-colors ${
            isActive ? 'bg-sky-50 text-sky-700' : 'text-slate-500 hover:bg-sky-50'
          }`}
          data-testid={`mobile-nav-toggle-${item.label}`}
        >
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      <div
        className={`overflow-hidden transition-all duration-200 ease-out ${open ? 'max-h-60 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}
      >
        <div className="pl-4 space-y-1 border-l-2 border-sky-100 ml-4">
          {item.children.map((child) => (
            <Link
              key={child.to + child.label}
              to={child.to}
              onClick={onNavigate}
              className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                location.pathname === child.to ? 'bg-sky-50 text-sky-700' : 'text-slate-600 hover:bg-sky-50'
              }`}
              data-testid={`mobile-nav-child-${child.label}`}
            >
              {child.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const settings = useSection('settings');
  const nav = useSection('navigation');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const menuItems = nav.menuItems || [];
  const logo = resolveImage(settings.logoUrl);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-lg shadow-sm border-b border-sky-100' : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <div className="relative">
              {logo && (
                <img
                  src={logo}
                  alt={settings.siteName || 'Logo'}
                  className="w-12 h-12 rounded-full shadow-md group-hover:scale-110 transition-transform duration-300 object-cover"
                />
              )}
              <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 animate-pulse ring-2 ring-white"></div>
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-extrabold text-lg text-sky-900">{settings.siteName || 'Kiosques à Eau'}</span>
              <span className="text-[9px] text-emerald-600 font-medium tracking-wide uppercase whitespace-nowrap">{settings.tagline}</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Navigation principale">
            {menuItems.map((l) => {
              const active = location.pathname === l.to;
              return <NavItem key={l.to + l.label} item={l} active={active} />;
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {nav.ctaLabel && (
              <Link to={nav.ctaLink || '/soutenir'} data-testid="navbar-cta-desktop">
                <Button className="water-btn bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white font-semibold px-5 rounded-xl border-0">
                  {nav.ctaLabel}
                </Button>
              </Link>
            )}
          </div>

          <button
            className="lg:hidden w-10 h-10 rounded-xl flex items-center justify-center hover:bg-sky-50 transition-colors"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            aria-expanded={open}
            data-testid="navbar-burger"
          >
            {open ? <X className="w-6 h-6 text-sky-700" /> : <Menu className="w-6 h-6 text-sky-700" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-sky-100 bg-white">
          <div className="px-4 py-4 space-y-1">
            {menuItems.map((l) => (
              <MobileNavItem key={l.to + l.label} item={l} onNavigate={() => setOpen(false)} />
            ))}
            {nav.ctaLabel && (
              <Link to={nav.ctaLink || '/soutenir'} className="block pt-2" data-testid="navbar-cta-mobile">
                <Button className="w-full bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-xl">
                  {nav.ctaLabel}
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
